<script>
import { mapActions, mapGetters } from 'vuex'
import table from './offer-in-group.table'
import { formatDate } from '@lib/utils/format-date'
import getExportedFilename from '@lib/utils/get-exported-filename'

export default {
  name: 'OfferGroup',
  page: {
    title: 'Offer Group Details',
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      offerGroup: {},
      backRoute: { name: 'offer-groups' },
      table: null,
      showDeleteDialog: false,
      progress: false,
    }
  },
  computed: {
    ...mapGetters('me', [
      'canUpdateOffer',
      'canDeleteOffer',
    ]),

    customComparisons() {
      return {
        string: {
          eq: 'contains',
        },
      }
    },

    isEnabled() {
      return this.offerGroup.enabled
    },

    offerGroupTags() {
      return this.offerGroup.tags ? this.offerGroup.tags.join(', ') : ''
    },

    getProviderName() {
      return this.offerGroup.provider ? this.offerGroup.provider.name : ''
    },
  },

  created() {
    this.fetchOfferGroup()
    this.table = table(this, this.id)
  },
  methods: {
    ...mapActions('offerGroup', [
      'getOfferGroup',
      'deleteOfferGroup',
    ]),

    async fetchOfferGroup() {
      this.loading = true
      const [err, response] = await this.getOfferGroup(this.id)

      if (err) {
        this.$router.replace(this.backRoute)
      } else {
        this.offerGroup = response
      }

      this.loading = false
    },

    formatDate(value, format) {
      return formatDate(value, format || 'DD/MM/YYYY')
    },

    onEdit() {
      this.$router.push({
        name: 'offer-group-update',
        params: { id: this.offerGroup.id },
      })
    },

    onRowClick(row) {
      this.$router.push({
        name: 'offer-details',
        params: { id: row.id },
      })
    },
    getExportedFilename() {
      return getExportedFilename(this.table.tableName)
    },
    async onDelete() {
      this.showDeleteDialog = false
      this.progress = true

      await this.deleteOfferGroup(this.offerGroup.id).then(response => {
        this.$notifier({
          response,
          errorTitle: 'Unable to delete offer group',
          successTitle: 'Offer group deleted',
          successMsg: 'Offer group successfully deleted.',
        }).then(success => {
          if (success) {
            this.$router.push({
              name: 'offer-groups',
            })
          }
        })
      })

      this.progress = false
    },
  },
}
</script>

<template>
  <main-layout
    v-loading="loading"
    :back="{
      name: 'offer-groups'
    }"
    title="Offer Group Details"
  >
    <template #header>
      <div :class="$style.actionsWrapper">
        <el-button
          v-if="canDeleteOffer"
          type="danger"
          :disabled="progress"
          class="el-icon-delete-solid"
          :class="$style.btn"
          circle
          @click.stop="showDeleteDialog = true"
        />

        <el-button
          v-if="canUpdateOffer"
          :class="$style.btn"
          type="primary"
          circle
          :disabled="progress"
          @click="onEdit"
        >
          <img
            :class="$style.editBtnIcon"
            src="../../assets/pen.svg"
          >
        </el-button>
      </div>
    </template>
    <data-box
      :status="false"
      :header="offerGroup.name || 'Offer Group Name'"
    >
      <div :class="$style.mb">
        <data-list :class="!isEnabled && $style.inactive">
          <dt>Enabled</dt>
          <dd>{{ offerGroup.enabled ? 'Yes' : 'No' }}</dd>

          <dt>Offer Group Name</dt>
          <dd>{{ offerGroup.name || '' }}</dd>

          <dt>Offer Group Status </dt>
          <dd>
            <status :status="offerGroup.status === 'active'" />
          </dd>

          <dt>Provider</dt>
          <dd>
            {{ getProviderName }}
          </dd>

          <dt>Offer Group Created</dt>
          <dd>
            {{ formatDate(offerGroup.createdAt, 'DD/MM/YYYY') || '' }}
          </dd>

          <dt>Offer Group Start Date</dt>
          <dd>
            {{ formatDate(offerGroup.startsAt, 'DD/MM/YYYY') || '' }}
          </dd>

          <dt>Offer Group End Date</dt>
          <dd>
            {{ formatDate(offerGroup.endsAt, 'DD/MM/YYYY') || '' }}
          </dd>

          <dt>Offer Group Redemption Limit</dt>
          <dd>
            {{ offerGroup.redemptionLimit || '' }}
          </dd>

          <dt>Offer Group Tags</dt>
          <dd>
            {{ offerGroupTags }}
          </dd>
        </data-list>
      </div>
      <h2 :class="$style.offers">
        Offers in Group
      </h2>
      <table-layout
        :table-name="table.tableName"
        :processor="table.processor"
        :filters="table.filters"
        :initial-filters="$route.params.filters"
        :columns="table.columns"
        :fragments="false"
        :quantity="[25, 50, 100, 200]"
        :export-filename="getExportedFilename"
        :custom-comparisons="customComparisons"
        :multisort="false"
        hider-header-visible
        :disabled="true"
        @row-click="onRowClick"
      />
    </data-box>
    <state-dialog
      :visible.sync="showDeleteDialog"
      title="Delete Offer group"
      brand="danger"
    >
      <el-icon
        slot="icon"
        name="price-tag"
      />
      <el-button
        slot="subicon"
        icon="el-icon-delete"
        type="danger"
        circle
      />
      <div :class="$style.msg">
        Are you sure you wish to delete this offer group {{ offerGroup.name }}?
      </div>
      <div class="modal__footer">
        <el-button
          type="danger"
          class="el-button--wide"
          @click="onDelete"
        >
          Delete offer
        </el-button>
      </div>
    </state-dialog>
  </main-layout>
</template>

<style lang="scss" module>
.inactive {
  opacity: 0.4;
}

.actionsWrapper {
  display: flex;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  &:first-child {
    margin-right: 15px;
  }
}

.editBtnIcon {
  width: rem(14px);
  height: rem(14px);
}

.mb {
  margin-bottom: 50px;
}

.offers {
  padding-top: 40px;
  border-top: 1px solid gray;
}
</style>
